import {
  getPlanRecommendations,
  getShownPlanRecommendations,
  updateMedApiUser,
} from 'actions/Plans';
import { useAppDispatch, useAppSelector } from 'lib/hooks';
import { selectCurrentLocation } from 'slices/locationSlice';
import {
  setGapPlans,
  setMaPlans,
  setPdpPlans,
} from 'slices/recommendationSlice';
import {
  selectMedApiUserParams,
  selectMedSuppRequestParams,
} from 'slices/selectors';
import { SUPPORTED_PLAN_YEARS } from 'lib/planYears';
import { captureGapPdpMissingPdp } from 'lib/analytics';

export const useRecommendation = ({
  recordAsShown = false,
}: { recordAsShown?: boolean } = {}) => {
  const dispatch = useAppDispatch();

  const location = useAppSelector(selectCurrentLocation);
  const userParams = useAppSelector(selectMedApiUserParams);
  const msParams = useAppSelector(selectMedSuppRequestParams);

  return async () => {
    try {
      await updateMedApiUser(userParams);

      const recommendationAction = recordAsShown
        ? getShownPlanRecommendations
        : getPlanRecommendations;

      // Store each year as soon as it returns. Pathfinder /3m reads Redux
      // once, so a slow or hung year must not hold back years that already
      // succeeded.
      const settled = await Promise.allSettled(
        SUPPORTED_PLAN_YEARS.map(async (year) => {
          try {
            const recommendations = await recommendationAction(
              userParams.id as string,
              location,
              msParams,
              year
            );
            dispatch(
              setMaPlans({
                hasResults: true,
                results: recommendations.mapd,
                planYear: year,
              })
            );
            dispatch(
              setGapPlans({
                hasResults: true,
                results: recommendations.gap,
                planYear: year,
              })
            );
            dispatch(
              setPdpPlans({
                hasResults: true,
                results: recommendations.pdp,
                planYear: year,
              })
            );
            const missingPdp =
              recommendations.gap.length > 0 &&
              recommendations.pdp.length === 0;
            return missingPdp ? year : null;
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Error fetching recommendations:', err);
            return null;
          }
        })
      );

      const missingPdpYears = settled.flatMap((result) =>
        result.status === 'fulfilled' && typeof result.value === 'number'
          ? [result.value]
          : []
      );
      if (missingPdpYears.length > 0) {
        captureGapPdpMissingPdp(missingPdpYears);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error fetching recommendations:', err);
    }
  };
};
